import classNames from 'classnames'
import Header from './components/Header'
import { AppState, AppStateActions } from './state/AppState'
import ConfiguratorScreen from './configurator/ConfiguratorScreen'
import ListScreen from './list/ListScreen'
import { SharedListPreviewScreen } from './list/SharedListScreen'
import SharedBomScreen from './list/SharedBomScreen'
import React from 'react'
import useAnimateOutState from './hooks/useAnimateOutState'
import useTimeoutIds from './hooks/useTimeoutIds'
import ConfiguratorAppContext, { ConfiguratorAppContextShape } from './ConfiguratorAppContext'
import BannerList from './BannerList'
import { ProductDataContext } from './ProductDataContext'
import { inflateListItems } from './products/productUtils'
import { ConfiguredProduct } from './list/ListTypes'
import { AppStateReducer } from './state/AppStateReducer'
import { ProductDebugList } from './ProductDebugList'
import { resolveDeeplinkProduct } from './configurator/resolveDeeplinkProduct'
import SelectUserTypePopup from './list/SelectUserTypePopup'
import DebugHeader from './debug/DebugHeader'
import AlertPopup from './components/AlertPopup'
import { CompositeContextProvider, ContextConfig } from './components/CompositeContextProvider'
import LoginPopup from './list/LoginPopup'
import { onDownloadBom } from './utils/downloadList'
import DownloadListPopup from './list/DownloadListPopup'
import { getBomProductsForInflatedItem, InflatedProductBomWithRoom } from './bom/bomUtils'
import { ENABLE_SIMULATED_LOGIN_FLOW } from './environment'
import { useProductPrices } from './hooks/useProductPrices'
import { useAppStateAnalytics } from './useAppStateAnalytics'
import WallColorSelector from './WallColorSelector'
import { ImagePreloader } from './components/ImagePreloader'
import { IconPlusWhite } from './components/icons'
import { getInitialAppStateFactory } from './appStateHelpers'
import { UserType } from './types/userTypes'
import { setLocalStorageSavedState } from './state/LocalStorageSavedState'
import { logDebug } from './debug/debugLogging'
import { setDebugModeSavedPref } from './debug/debugModeUtils'
import { ProductSeries } from './types/productTypes'
import { AnalyticsContext } from './analytics/AnalyticsContext'
import { usePageViewEvent } from './analytics/usePageViewEvent'
import { History } from 'history'

interface ConfiguratorAppProps {
  deepLinkItemNo?: string | null
  isAuthenticated: boolean
  shouldShowPackQuantityInCustomise: boolean
  clearList: boolean
  lockToElectricianUserType: boolean
  proSeriesEnabled: boolean
  addingRoomsEnabled: boolean
  canPersistUserData: boolean
  defaultAppState: AppState
  productSeries?: ProductSeries
  routeHistory?: History
}

const ConfiguratorApp: React.FunctionComponent<ConfiguratorAppProps> = ({
  deepLinkItemNo,
  isAuthenticated,
  shouldShowPackQuantityInCustomise,
  clearList,
  lockToElectricianUserType,
  proSeriesEnabled,
  addingRoomsEnabled,
  canPersistUserData,
  defaultAppState,
  productSeries,
}) => {
  const Products = React.useContext(ProductDataContext)

  const deepLinkData = React.useMemo(() => resolveDeeplinkProduct(deepLinkItemNo ?? null, Products), [Products, deepLinkItemNo])

  const [productNotFoundWarningShown, setProductNotFoundWarningShown] = React.useState(deepLinkItemNo && !deepLinkData)

  const [appState, dispatchAppAction] = React.useReducer(
    AppStateReducer,
    null,
    getInitialAppStateFactory(
      Products,
      defaultAppState,
      {
        deepLinkData,
        isAuthenticated,
        clearList,
        overrideUserType: lockToElectricianUserType ? UserType.Electrician : null,
        overrideProductSeries: proSeriesEnabled ? (productSeries ?? null) : ProductSeries.Iconic,
        canLoadLocalStorageData: canPersistUserData,
      })
  )

  // Sync query param when series changes internally.
  // NOTE: There is intentionally no reverse effect (URL → state) here.
  // The series URL param is only ever written by this effect itself, so reacting
  // to URL changes would create a feedback loop. The initial series value is
  // already seeded from the URL param via getInitialAppStateFactory.
  //
  // We use window.history.replaceState directly (not routeHistory.replace) to avoid
  // react-router prepending PUBLIC_URL/basename to the pathname, which would add
  // '/build-iconic/' to URLs served from '/clipsal-switch-socket-builder/'.
  React.useEffect(() => {
    if (proSeriesEnabled) {
      const currentSeries = appState.userState.selectedProductSeries
      const params = new URLSearchParams(window.location.search)
      const currentParam = params.get('series')

      const targetParam = currentSeries === ProductSeries.Pro ? 'pro' : 'iconic'

      if (currentParam !== targetParam) {
        params.set('series', targetParam)
        window.history.replaceState(
          window.history.state,
          '',
          window.location.pathname + '?' + params.toString()
        )
      }
    }
  }, [appState.userState.selectedProductSeries, proSeriesEnabled])

  // save user state to local storage on change
  React.useEffect(() => {
    if (canPersistUserData) {
      logDebug('savestate', 'saving userState', appState.userState)
      setLocalStorageSavedState(appState.userState)
    }
  }, [appState.userState, canPersistUserData])

  // save debug setting pref
  React.useEffect(() => {
    setDebugModeSavedPref(appState.debugMode)
  }, [appState.debugMode])

  useAppStateAnalytics(appState)

  const { editingProductId, isEditing, userState } = appState

  usePageViewEvent({ userType: userState.userType, pageCategory: 'selector' })

  const { configuredProducts: listProducts, rooms, wallColor } = userState

  const listShown = appState.screen === 'list'

  const sharingListShown = appState.screen === 'sharingList'

  const sharingBomShown = appState.screen === 'sharingBom'
  const wallColorShown = appState.screen === 'wallColor'

  const analyticsLogger = React.useContext(AnalyticsContext)

  const context: ConfiguratorAppContextShape = {
    onClickHome: () => { return },
    onClickAccount: () => { return },
    onClickList: () => {
      analyticsLogger.log({
        event: 'cta_click',
        cta_name: 'My List',
        cta_link: null,
        cta_category: 'Button',
        cta_location: 'Header'
      })

      dispatchAppAction({ action: AppStateActions.ToggleListDisplayed })
    },
    onClickShareList: () => { dispatchAppAction({ action: AppStateActions.OpenSharingList }) },
    onClickShareBom: () => {
      analyticsLogger.log({
        event: 'cta_click',
        cta_name: 'BOM',
        cta_link: null,
        cta_category: 'Button',
        cta_location: 'Header'
      })

      dispatchAppAction({ action: AppStateActions.OpenSharingBom })
    },
    onClickWallColor: () => {
      analyticsLogger.log({
        event: 'cta_click',
        cta_name: 'Wall Colour',
        cta_link: null,
        cta_category: 'Button',
        cta_location: 'Header'
      })

      dispatchAppAction({ action: AppStateActions.OpenWallColor })
    },
    dispatch: dispatchAppAction,
    listShown,
    sharingListShown,
    sharingBomShown,
    wallColorShown,
    userType: userState.userType,
    listName: userState.listName,
    listId: userState.listId,
    wallColor: userState.wallColor,
    debugMode: appState.debugMode,
    listAccessible: userState.hasListBeenShown,
    renameListTutorialDismissed: userState.renameListTutorialDismissed,
    typeSelectorTutorialDismissed: userState.typeSelectorTutorialDismissed,
    shouldShowCustomiseHint: userState.customiseHintShownCount < 3,
    shouldShowCashSales: appState.showCashSales,
    optionalWiserMode: appState.optionalWiserMode,
    selectedProductSeries: appState.userState.selectedProductSeries,
    moreInfo: appState.userState.moreInfo,
    isAuthenticated: appState.userState.isAuthenticated,
    shouldShowPackQuantityInCustomise: shouldShowPackQuantityInCustomise,
    userTypeSelectionEnabled: !lockToElectricianUserType,
    proSeriesEnabled: proSeriesEnabled,
    addingRoomsEnabled: addingRoomsEnabled,
    hasOptedOutOfWiserHubPrompt: appState.hasOptedOutOfWiserHubPrompt,
    hasOptedOutOfMomentarySwitchesPrompt: appState.hasOptedOutOfMomentarySwitchesPrompt
  }

  const onSaveProduct = ((product: ConfiguredProduct) => {
    dispatchAppAction({
      action: AppStateActions.SaveProduct,
      product,
    })
  })

  const [shouldRenderList, listExpanded] = useAnimateOutState(listShown, 550)
  const [shouldRenderSharingList, sharingListExpanded] = useAnimateOutState(sharingListShown, 550)
  const [shouldRenderSharingBom, sharingBomExpanded] = useAnimateOutState(sharingBomShown, 550)
  const [shouldRenderWallColor, wallColorExpanded] = useAnimateOutState(wallColorShown, 550)

  const products = listProducts.map(it => it.configuredProduct)

  const editingProduct = products.find(it => it.uid === editingProductId) ?? null

  const [setJustAdded, bannerIds] = useTimeoutIds(3000)

  const onAddProductToList = ((product: ConfiguredProduct) => {
    onSaveProduct(product)
    setJustAdded(product.uid)
  })

  const setWallColor = (color: string) => {
    dispatchAppAction({ action: AppStateActions.ChangeWallColor, color })
  }

  const hideWallColourSelector = () => {
    if (wallColorShown) {
      context.onClickWallColor()
    }
  }

  const [debugListShown, setDebugListShown] = React.useState(false)
  const [showLoginPrompt, setShowLoginPrompt] = React.useState(false)
  const [showDownloadBomPopup, setShowDownloadBomPopup] = React.useState(false)
  const [isUserTypeSelectorEnabled, setIsUserTypeSelectorEnabled] = React.useState(true)

  const configuratorAppContextConfig: ContextConfig<ConfiguratorAppContextShape> = { context: ConfiguratorAppContext, value: context }

  const { priceTypeLabel, priceMap, getPackQuantity } = useProductPrices([])

  const handleDownloadBom = () => {
    if (!ENABLE_SIMULATED_LOGIN_FLOW) {
      onDownloadBom(getBomProducts(), getPackQuantity, { priceMap, label: priceTypeLabel })
    } else if (isAuthenticated) {
      setShowDownloadBomPopup(true)
    } else {
      setShowLoginPrompt(true)
    }
  }

  const getBomProducts = (): readonly InflatedProductBomWithRoom[] => {
    const products = inflateListItems(listProducts, Products)
    return products.map(product => ({
      ...getBomProductsForInflatedItem(product),
      roomId: product.roomId,
    })) ?? []
  }

  return (
    <CompositeContextProvider
      contexts={[
        configuratorAppContextConfig,
      ]}
    >
      <ImagePreloader
        srcs={[
          IconPlusWhite,
        ]}
      />
      <DebugHeader onClickDebugList={() => setDebugListShown(true)} />
      <Header isUserTypeSelectorEnabled={isUserTypeSelectorEnabled} />
      {
        !userState.userType && (
          <SelectUserTypePopup
            userType={userState.userType}
            onConfirm={userType => {
              dispatchAppAction({ action: AppStateActions.SelectUserType, userType: userType })
            }}
          />
        )
      }
      {
        productNotFoundWarningShown && (
          <AlertPopup
            onClose={() => setProductNotFoundWarningShown(false)}
            buttonLabel="Okay"
            message="Sorry, we couldn't find that product."
          />
        )
      }
      <div
        className="mainContentOuter"
        style={{
          // paddingTop: wallColorExpanded ? '100px' : undefined,
          transition: '300ms',
        }}
      >
        {
          debugListShown && (
            <ProductDebugList products={Products} onClose={() => setDebugListShown(false)} />
          )
        }
        {
          shouldRenderWallColor && (
            <WallColorSelector
              isExpanded={wallColorExpanded}
              wallColorOptions={Products.wallColorOptions}
              wallColor={wallColor}
              setWallColor={(color: string) => setWallColor(color)}
              onClose={() => hideWallColourSelector()}
            />
          )
        }
        <div className="mainContent" onClick={() => hideWallColourSelector()}>
          <BannerList
            ids={bannerIds}
            onClick={() => dispatchAppAction({ action: AppStateActions.ToggleListDisplayed })}
            showButton={false}
          />
          <ConfiguratorScreen
            isEntryFromDeepLink={deepLinkData !== null}
            key={'new product'}
            editingProduct={null}
            deepLinkData={deepLinkData}
            isInEditMode={false}
            shouldDisableUserTypeSelectionWhenEditingButtons
            onAddProduct={onAddProductToList}
            onSetIsUserTypeSelectorEnabled={setIsUserTypeSelectorEnabled}
          />
          {
            shouldRenderList && (
              <div
                className={classNames('ListScreenWrapper', { collapsed: !listExpanded })}
                onClick={e => {
                  dispatchAppAction({ action: AppStateActions.SetListDisplayed, displayed: false })
                  e.stopPropagation()
                }}
              >
                <ListScreen
                  products={listProducts}
                  focusedItemUid={appState.focusedListItemUid}
                  rooms={userState.rooms}
                />
              </div>
            )
          }
          {
            shouldRenderSharingList && (
              <div className={classNames('ListScreenWrapper', { collapsed: !sharingListExpanded })}>
                <SharedListPreviewScreen
                  products={listProducts}
                  rooms={userState.rooms}
                  listName={userState.listName}
                />
              </div>
            )
          }
          {
            shouldRenderSharingBom && (
              <div
                className={classNames('ListScreenWrapper', { collapsed: !sharingBomExpanded })}
                onClick={e => {
                  context.onClickShareBom()
                  e.stopPropagation()
                }}
              >
                <SharedBomScreen
                  products={getBomProducts()}
                  rooms={rooms}
                  scrollable={true}
                  onClickDownloadList={() => {
                    handleDownloadBom()
                  }}
                />
              </div>
            )
          }
          {
            showLoginPrompt && (
              <LoginPopup
                title="Log in to download BOM"
                onClose={() => { setShowLoginPrompt(false) }}
              />

            )}
        </div>
      </div>
      {isEditing && (
        <div className="modalEditor">
          <ConfiguratorScreen
            isEntryFromDeepLink={deepLinkData !== null}
            key={'edit product'}
            editingProduct={editingProduct}
            isInEditMode
            shouldDisableUserTypeSelectionWhenEditingButtons={false}
            onAddProduct={onSaveProduct}
            onCancel={editingProduct ? () => {
              onSaveProduct(editingProduct)
            } : undefined}
            onSetIsUserTypeSelectorEnabled={setIsUserTypeSelectorEnabled}
          />
        </div>
      )}
      {showDownloadBomPopup && (
        <DownloadListPopup
          onClose={() => setShowDownloadBomPopup(false)}
          onSubmit={(value) => {
            onDownloadBom(getBomProducts(), getPackQuantity, { priceMap, label: priceTypeLabel }, value)
          }}
        />
      )}
    </CompositeContextProvider>
  )
}

export default ConfiguratorApp
